import { useParams, useNavigate } from "react-router-dom"
import { useEffect, useState } from "react"
import { fetchBlogBySlug } from "pages/blog/components/slugs"
import Icon from '../../components/AppIcon';
import Footer from 'pages/home/components/Footer';
import { FaTwitter, FaFacebook, FaLinkedin, FaLink, FaArrowRight, FaArrowLeft } from 'react-icons/fa';
import { Helmet } from 'react-helmet-async'

const escapeHtml = (unsafe) => {
  if (typeof unsafe !== 'string') return unsafe;
  return unsafe.replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

const renderList = (items, type, meta = {}) => {
  if (!items || !Array.isArray(items)) return "";
  if (type === "checklist") {
    return `
<ul class="list-none space-y-2 my-5">
${items
        .map(
          (i) =>
            `<li class="flex items-start text-gray-500">
<input type="checkbox" ${i.meta?.checked ? "checked" : ""
            } class="mr-3 mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 pointer-events-none" /> 
<span class="${i.meta?.checked ? "text-gray-500" : ""}">${i.content || ""}</span>
</li>`
        )
        .join("")}
</ul>
`
  }
  const tag = type === "ordered" ? "ol" : "ul"
  const listStyle = type === "ordered" ? "list-decimal pl-6" : "list-disc pl-6"

  return `
<${tag} class="${listStyle} space-y-2 my-5 text-gray-700">
${items.map((i) => `<li class="leading-relaxed">${i.content || ""}</li>`).join("")}
</${tag}>
`
}

const renderTable = (table) => {
  if (!table || !table.content) return "";
  return `
<div class="overflow-x-auto my-6 shadow-md rounded-lg">
<table class="min-w-full divide-y divide-gray-200 border-collapse">
<tbody class="bg-white divide-y divide-gray-200">
${table.content
      .map(
        (row, rowIndex) => `
<tr class="${rowIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white'}">
${row.map((col) => `<td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700">${col || ""}</td>`).join("")}
</tr>
`
      )
      .join("")}
</tbody>
</table>
</div>
`
}

const renderBlocks = (blocks, coverImageUrl) => {
  if (!blocks || !Array.isArray(blocks)) return "";
  let isFirstImage = true;

  return blocks
    .map((block) => {
      if (!block.data) return "";
      switch (block.type) {
        case "paragraph":
          return `<p class="my-5 leading-8 text-lg text-gray-700">${block.data.text || ""}</p>`
        case "header":
          const size = {
            1: "text-4xl font-extrabold mt-12 mb-6 text-gray-900",
            2: "text-3xl font-bold mt-10 mb-5 text-gray-900 border-b pb-2",
            3: "text-2xl font-semibold mt-8 mb-4 text-gray-800"
          }[block.data.level] || "text-xl font-semibold"
          return `<h${block.data.level} class="${size}">${block.data.text || ""}</h${block.data.level}>`
        case "delimiter":
          return `<div class="py-2 flex justify-center"><div class="border-t-2 border-gray-200 w-full rounded-full"></div></div>`
        case "image":
          const imageUrl = block.data.file?.url;
          if (!imageUrl) return "";
          if (imageUrl === coverImageUrl && isFirstImage) {
            isFirstImage = false;
            return "";
          }
          return `<img src="${imageUrl}" alt="Blog Image" class="w-full object-cover my-8 rounded-xl shadow-lg" />`
        case "list":
          return renderList(block.data.items, block.data.style, block.data.meta)
        case "code":
          const escapedCode = escapeHtml(block.data.code || "");
          return `<pre class="bg-gray-800 text-white p-6 my-6 rounded-xl overflow-x-auto shadow-lg text-base leading-relaxed"><code>${escapedCode}</code></pre>`
        case "warning":
          return `
<div class="border-l-4 border-amber-500 bg-amber-50 p-5 my-6 rounded-lg shadow-sm">
<b class="text-amber-800 text-lg flex items-center"><Icon name="ExclamationTriangleIcon" className="w-5 h-5 mr-2" />${block.data.title || ""}</b>
<p class="text-amber-700 mt-2">${block.data.message || ""}</p>
</div>
`
        case "raw":
          return block.data.html || ""
        case "layout":
          if (!block.data.content) return "";
          return `<div class="grid grid-cols-1 md:grid-cols-${block.data.content.length} gap-6 my-6">${block.data.content
            .map((column) => `<div class="bg-gray-50 p-6 rounded-xl shadow-inner">${renderBlocks(column.blocks, coverImageUrl)}</div>`)
            .join("")}</div>`
        case "table":
          return renderTable(block.data)
        default:
          return ""
      }
    })
    .join("")
}

const formatDate = (dateString) => {
  if (!dateString) return "Date not available";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid Date";
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  } catch (e) {
    return "Invalid Date";
  }
}

const BlogDetail = () => {
  const { slug } = useParams()
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const data = await fetchBlogBySlug(slug)
        setBlog(data)
      } catch (err) {
        console.error("Error fetching blog:", err);
      } finally {
        setLoading(false)
      }
    }
    fetchBlog()
  }, [slug])

  const handleGoBack = () => {
    navigate(-1);
  };

  if (loading)
    return (
      <div className="flex justify-center items-center h-screen text-xl font-medium text-indigo-600">
        <Icon name="Loader" className="w-6 h-6 animate-spin mr-2" /> Loading Blog Content...
      </div>
    )
  if (!blog)
    return (
      <div className="flex justify-center items-center h-screen text-xl font-medium text-red-600">
        <Icon name="XCircleIcon" className="w-6 h-6 mr-2" /> No blog found for this link.
      </div>
    )

  const coverImageUrl = blog?.coverImage?.url || null;
  const html = renderBlocks(blog?.content?.blocks, coverImageUrl)
  const currentUrl = window.location.href;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(currentUrl).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }).catch((err) => {
      console.error('Could not copy text: ', err);
    });
  };

  const displayDate = formatDate(blog?.createdAt);

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{blog?.metaTitle}</title>
        <meta name="description" content={blog?.metaDescription} />
      </Helmet>
      <div className="max-w-4xl mx-auto px-4 py-6 bg-white shadow-2xl rounded-lg mt-18 mb-12 relative">

        <div className="absolute top-4 left-6">
          <button
            onClick={handleGoBack}
            className="flex items-center space-x-2 text-indigo-600 hover:text-indigo-800 transition duration-150 font-semibold text-lg p-2 "
            aria-label="Go Back">
            <FaArrowLeft className="w-5 h-5" />
            <span>Go Back</span>
          </button>
        </div>

        <div className="pt-8 pb-8 border-b border-gray-200 mb-8">

          <h1 className="text-5xl font-extrabold leading-tight text-gray-900 mb-5">
            {blog?.title ?? "Untitled Blog"}
          </h1>
          <p className="text-xl text-gray-600 mb-8 font-light">
            {blog?.subTitle ?? ""}
          </p>

          {coverImageUrl && (
            <img
              src={coverImageUrl}
              alt="Blog Cover"
              className="w-full h-[450px] object-cover mt-4 mb-10 rounded-xl shadow-2xl"
            />
          )}

          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between text-sm text-gray-500">

            <div className="flex flex-col space-y-3">
              <h1 className="font-bold text-gray-800 text-lg">Author</h1>

              <div className="flex items-center space-x-4">
                {blog?.authorImage?.url && (
                  <img
                    src={blog.authorImage.url}
                    alt={blog?.authorName ?? "Author"}
                    className="w-12 h-12 rounded-full object-cover border-2 border-indigo-400"
                  />
                )}

                <div>
                  <p className="font-semibold text-gray-800">{blog?.authorName ?? "Anonymous"}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-4 mt-4 sm:mt-14">
              <span className="text-gray-600 flex items-center">
                <Icon name="CalendarIcon" className="w-5 h-5 mr-1 text-indigo-500" />
                {displayDate}
              </span>

              <span className="w-1 h-1 bg-gray-300 rounded-full" />

              <span className="text-gray-600 flex items-center">
                <Icon name="ClockIcon" className="w-5 h-5 mr-1 text-indigo-500" />
                {blog?.readTime || "8 "}
              </span>

              <span className="w-1 h-1 bg-gray-300 rounded-full" />

              <span className="text-gray-600 flex items-center">
                <Icon name="EyeIcon" className="w-5 h-5 mr-1 text-indigo-500" />
                {blog?.views || "2.4K views"}
              </span>
            </div>
          </div>
        </div>

        <div
          className="blog-content"
          dangerouslySetInnerHTML={{ __html: html }}
        >
        </div>

        <div className="pt-10 mt-10 border-t border-gray-100">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center">

            <div className="flex items-center flex-wrap mb-6 lg:mb-0">
              {blog?.tags && blog.tags.length > 0 && (
                <>
                  <span className="font-semibold text-lg text-gray-800 mr-4">Tags:</span>
                  {blog.tags.slice(0, 5).map((tag) => (
                    <span
                      key={tag}
                      className="text-sm font-medium text-indigo-700 bg-indigo-100 rounded-full px-4 py-1 mr-2 mb-2 hover:bg-indigo-200 cursor-pointer transition duration-150"
                    >
                      {tag}
                    </span>
                  ))}
                </>
              )}
            </div>

            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <span className="hidden md:inline mr-2 text-gray-800 font-semibold">Share:</span>

              <a href={`https://twitter.com/intent/tweet?url=${currentUrl}&text=${encodeURIComponent(blog?.title || "")}`} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-blue-400 transition duration-150">
                <FaTwitter size={24} color="#1DA1F2" />
              </a>
              <a href={`https://www.facebook.com/sharer/sharer.php?u=${currentUrl}`} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-blue-700 transition duration-150">
                <FaFacebook size={24} color="#1877F2" />
              </a>
              <a href={`https://www.linkedin.com/company/zentirx/shareArticle?mini=true&url=${currentUrl}&title=${encodeURIComponent(blog?.title || "")}`} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-blue-800 transition duration-150">
                <FaLinkedin size={24} color="#0A66C2" />
              </a>

              <button
                onClick={handleCopyLink}
                className={`transition duration-150 ${isCopied ? 'text-blue-500' : 'text-gray-500 hover:text-gray-800'}`}
                title={isCopied ? "Link Copied!" : "Copy Link"}
              >
                <FaLink size={24} color={isCopied ? "#3B82F6" : "#4B5563"} />
              </button>

              <div className="flex items-center space-x-4 border-l pl-4 ml-4">
                <button className="flex items-center space-x-1 p-2 rounded-full hover:bg-red-50 transition duration-150">
                  <Icon name="HeartIcon" className="w-6 h-6 text-gray-500 hover:text-red-500" />
                  <span className="text-gray-700 font-semibold">{blog?.likes || 0}</span>
                </button>
                <button className="flex items-center space-x-1 p-2 rounded-full hover:bg-indigo-50 transition duration-150">
                  <Icon name="BookmarkIcon" className="w-6 h-6 text-gray-500 hover:text-indigo-600" />
                  <span className="text-gray-700 font-semibold hidden sm:inline">Save</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 border-b-2 border-indigo-100 pb-2">More Articles You Might Like</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <ArticleCard title="Modern JavaScript Frameworks in 2024" category="Technology" date="March 15, 2024" readTime="4 min" icon="ImageIcon" />
            <ArticleCard title="Serverless Architecture: Best Practices" category="Cloud" date="March 8, 2024" readTime="5 min" icon="CloudIcon" />
            <ArticleCard title="UI/UX Design Trends for 2024" category="UI/UX" date="March 5, 2024" readTime="6 min" icon="Webhook" />
          </div>
        </div>
      </div>
      <Footer />
    </div>
  )
}

const ArticleCard = ({ title, category, date, readTime, icon }) => (
  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition duration-300 transform hover:-translate-y-1">
    <div className="h-40 bg-indigo-50 flex items-center justify-center text-gray-500">
      <Icon name={icon} className="w-10 h-10 text-indigo-400" />
    </div>
    <div className="p-5">
      <span className="text-sm font-semibold text-indigo-700 bg-indigo-100 rounded-full px-3 py-1 mb-2 inline-block">{category}</span>
      <h3 className="text-lg font-bold mt-1 text-gray-900 hover:text-indigo-600 transition duration-150 cursor-pointer">{title}</h3>
      <p className="text-sm text-gray-500 mt-3">{date} • {readTime}</p>
    </div>
  </div>
);

export default BlogDetail;